import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CircleMarker,
  MapContainer,
  Pane,
  Polygon,
  Polyline,
  Popup,
  WMSTileLayer,
  ZoomControl,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./AqMap.css";
import { fetchAirQuality, fetchWeather, fetchWindToday } from "./api";
import { LMI_ATTRIBUTION, LMI_WMS_URL, baseParams } from "./basemap";
import { PlaceLabels } from "./PlaceLabels";
import { useGoogleUser } from "./auth";
import { LayerPanel, useLayers } from "./LayerPanel";
import { NOISE_ATTRIBUTION, NOISE_PARAMS, NOISE_WMS_URL } from "./noise";
import { PollenSites } from "./PollenSites";
import { fetchRoads } from "./roads";
import { RoadStations } from "./RoadStations";
import { fetchListings, parsePostcodes } from "./sale";
import { SaleListings } from "./SaleListings";
import { fetchSoilSites } from "./soil";
import { SoilSites } from "./SoilSites";
import { driftAreas, driftLine, driftOpacity, driftPace } from "./plume";
import { LEVELS, hoursWithData, stationSeverity } from "./severity";
import { CALM_MS, compass, compassWords, toKmh, windAt } from "./wind";
import { Timeline, formatHour, todayHours } from "./Timeline";
import { usePolling } from "./usePolling";
import { WindField } from "./WindField";

const REYKJAVIK = [64.11, -21.85];
const ICELAND_BOUNDS = [
  [62.8, -25.5],
  [67.0, -12.5],
];
const REFRESH_MS = 10 * 60 * 1000;
const POLLING = { refreshMs: REFRESH_MS, retryMs: 60 * 1000 };
// Listings change slowly, and each refresh reads many result pages
const SALE_POLLING = { refreshMs: 30 * 60 * 1000, retryMs: 5 * 60 * 1000 };
// Stable empty list for hours without wind data, so memos don't rerun
const NO_WEATHER = [];
const NO_STATIONS = [];

const darkQuery = "(prefers-color-scheme: dark)";

const isDark = () => {
  const theme = document.documentElement.dataset.theme;
  if (theme) return theme === "dark";
  return window.matchMedia?.(darkQuery).matches ?? false;
};

// Follows the OS setting and a data-theme override on <html>
const useDarkMode = () => {
  const [dark, setDark] = useState(isDark);
  useEffect(() => {
    const update = () => setDark(isDark());
    const media = window.matchMedia?.(darkQuery);
    media?.addEventListener("change", update);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => {
      media?.removeEventListener("change", update);
      observer.disconnect();
    };
  }, []);
  return dark;
};

const formatTime = (date) =>
  date.toLocaleTimeString("is-IS", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });

// "NO2" -> NO<sub>2</sub>, "NOX as NO2" -> NO<sub>x</sub> as NO<sub>2</sub>;
// "PM2.5" is left as is
const formatParam = (param) =>
  param.startsWith("PM")
    ? param
    : param
        .split(/(?<=[A-Z])([23X])/)
        .map((part, i) =>
          i % 2 ? <sub key={i}>{part.toLowerCase()}</sub> : part,
        );

const formatUnit = (unit) => unit?.replace(/m3$/, "m³");

const formatValue = (value) =>
  Math.abs(value) >= 10 ? value.toFixed(0) : value.toFixed(1);

const Summary = ({ markers }) => {
  const rated = markers.filter((m) => m.severity.level !== null);
  if (rated.length === 0) return <p className="aq-panel__lede">Loading…</p>;

  const good = rated.filter((m) => m.severity.level === 0).length;
  const worst = rated.reduce((a, b) =>
    b.severity.level > a.severity.level ? b : a,
  );

  return (
    <p className="aq-panel__lede">
      <span className="aq-mono">{good}</span> of{" "}
      <span className="aq-mono">{rated.length}</span> reporting stations are
      good.
      {worst.severity.level > 0 && (
        <>
          {" "}
          Worst is {worst.name}:{" "}
          <strong>{worst.severity.label.toLowerCase()}</strong> from{" "}
          {formatParam(worst.severity.worst.param)} at{" "}
          <span className="aq-mono">
            {formatValue(worst.severity.worst.value)}
          </span>{" "}
          {formatUnit(worst.severity.worst.unit)}.
        </>
      )}
    </p>
  );
};

// "Air is moving south-southeast at about 13 km/h"
const airMovement = (wind) =>
  wind.speed < CALM_MS
    ? "The air is almost still"
    : `Air is moving ${compassWords(wind.towards)} at about ${Math.round(toKmh(wind.speed))} km/h`;

const WindNote = ({ wind }) =>
  wind ? (
    <p className="aq-panel__wind">
      <span
        className="aq-panel__arrow"
        style={{ transform: `rotate(${wind.towards}deg)` }}
        aria-hidden="true"
      >
        ↑
      </span>
      <span>
        {airMovement(wind)} over Reykjavík (wind{" "}
        <span className="aq-mono">{wind.speed.toFixed(1)} m/s</span> from{" "}
        {compass(wind.from)}).
      </span>
    </p>
  ) : null;

// Rated pollutants first, worst at the top; unrated ones (NO, NOₓ, CO, PM1)
// follow alphabetically
const sortReadings = (readings) =>
  [...readings].sort(
    (a, b) =>
      (b.level ?? -1) - (a.level ?? -1) || a.param.localeCompare(b.param),
  );

// Six segments, one per level; segments up to the reading's level are filled
// in its colour so position on the scale reads without relying on hue.
const LevelMeter = ({ level }) => {
  if (level === null) {
    return <span className="aq-meter__unrated">Not rated</span>;
  }
  const { color, label } = LEVELS[level];
  return (
    <span className="aq-meter">
      <span className="aq-meter__track" aria-hidden="true">
        {LEVELS.map((_, i) => (
          <span
            key={i}
            className="aq-meter__segment"
            style={i <= level ? { background: color } : undefined}
          />
        ))}
      </span>
      <span className="aq-meter__label">{label}</span>
    </span>
  );
};

const StationPopup = ({ station }) => {
  const { severity, wind } = station;
  return (
    <div className="aq-popup">
      <p className="aq-popup__level">
        <span
          className="aq-swatch"
          style={{ background: severity.color }}
          aria-hidden="true"
        />
        {severity.label}
      </p>
      <h3 className="aq-popup__name">{station.name}</h3>
      {severity.readings.length > 0 && (
        <table className="aq-popup__readings">
          <thead>
            <tr>
              <th scope="col">Pollutant</th>
              <th scope="col">Now</th>
              <th scope="col">Level</th>
            </tr>
          </thead>
          <tbody>
            {sortReadings(severity.readings).map((r) => (
              <tr
                key={r.param}
                className={
                  r.param === severity.worst?.param ? "is-driver" : undefined
                }
              >
                <th scope="row">{formatParam(r.param)}</th>
                <td className="aq-popup__value">
                  {formatValue(r.value)} <span>{formatUnit(r.unit)}</span>
                </td>
                <td>
                  <LevelMeter level={r.level} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="aq-popup__wind">
        {wind
          ? `${airMovement(wind)} here (wind ${wind.speed.toFixed(1)} m/s from ${compass(wind.from)}).`
          : "No weather station close enough to estimate the wind."}
      </p>
    </div>
  );
};

export const AqMap = () => {
  const [layers, setLayer] = useLayers();
  // Contaminated soil register, fetched the first time the layer is shown
  const [soilSites, setSoilSites] = useState(null);
  const [soilError, setSoilError] = useState(null);
  // null is the live view; otherwise an hour today (ms since epoch)
  const [at, setAt] = useState(null);
  const [windToday, setWindToday] = useState(null);
  const [windTodayLoading, setWindTodayLoading] = useState(false);
  const theme = useDarkMode() ? "dark" : "light";
  // A new params object makes Leaflet redraw every tile, so keep it stable
  const basemapParams = useMemo(() => baseParams(theme), [theme]);

  // Each source is only fetched, and refreshed, while a layer that uses it
  // is shown. Drift needs the wind even when the arrows are hidden.
  const needsWind = layers.wind || (layers.air && layers.drift);
  const air = usePolling(fetchAirQuality, layers.air, POLLING);
  const wind = usePolling(fetchWeather, needsWind, POLLING);
  const stations = air.data ?? NO_STATIONS;

  // Private listings: only once signed in, for the chosen postcodes
  const google = useGoogleUser(layers.sale);
  const salePostcodes = parsePostcodes(layers.salePostcodes).join(",");
  const fetchSale = useCallback(
    () => fetchListings(google.user, salePostcodes.split(",")),
    [google.user, salePostcodes],
  );
  const sale = usePolling(
    fetchSale,
    layers.sale && Boolean(google.user) && salePostcodes !== "",
    SALE_POLLING,
  );
  // Road weather/traffic stations and notices from umferdin.is
  const roads = usePolling(fetchRoads, layers.roads, POLLING);
  const weather = wind.data ?? NO_WEATHER;

  // Shown sources only: an error from a hidden layer isn't the viewer's
  // concern
  const failed = [
    layers.air && air.error && "air quality",
    needsWind && wind.error && "wind",
  ].filter(Boolean);
  const error = failed.length ? failed.join(" and ") : null;
  const fetchedAt = Math.max(
    (layers.air && air.fetchedAt) || 0,
    (needsWind && wind.fetchedAt) || 0,
  );
  const updatedAt = fetchedAt ? new Date(fetchedAt) : null;

  // Today's wind is only fetched once the timeline is used with a layer that
  // needs wind, and again after each live wind refresh so new hours appear
  useEffect(() => {
    if (at === null || !needsWind || windTodayLoading) return;
    if (windToday && windToday.fetchedAt >= (wind.fetchedAt ?? 0)) return;
    setWindTodayLoading(true);
    fetchWindToday()
      .then((byHour) => setWindToday({ byHour, fetchedAt: Date.now() }))
      .catch((err) => console.error("Error fetching today's wind:", err))
      .finally(() => setWindTodayLoading(false));
  }, [at, needsWind, windToday, windTodayLoading, wind.fetchedAt]);

  useEffect(() => {
    if (!layers.soil || soilSites) return;
    setSoilError(null);
    fetchSoilSites()
      .then(setSoilSites)
      .catch((err) => {
        console.error("Error fetching contaminated soil sites:", err);
        setSoilError(err.message);
      });
  }, [layers.soil, soilSites]);

  const activeWeather =
    at === null ? weather : (windToday?.byHour[at] ?? NO_WEATHER);

  const markers = useMemo(
    () =>
      stations
        .map((station) => ({
          ...station,
          severity: stationSeverity(station, at),
          wind: windAt(station.lat, station.lon, activeWeather),
        }))
        // Draw worse stations last so they sit on top
        .sort((a, b) => (a.severity.level ?? -1) - (b.severity.level ?? -1)),
    [stations, activeWeather, at],
  );

  const counts = useMemo(() => {
    const byLabel = {};
    markers.forEach((m) => {
      byLabel[m.severity.label] = (byLabel[m.severity.label] || 0) + 1;
    });
    return byLabel;
  }, [markers]);

  // Good air needs no drift; only show it where there is something to follow
  const drifting =
    layers.air && layers.drift
      ? markers.filter((m) => m.wind && m.severity.level > 0)
      : [];

  const reykjavikWind = useMemo(
    () => windAt(REYKJAVIK[0], REYKJAVIK[1], activeWeather),
    [activeWeather],
  );

  // Past hours the timeline can show, before "Now". With air quality on,
  // the hours that have readings (UST publishes each hour about 17 minutes
  // after it ends); with only wind, every hour so far today. The newest hour
  // is what "Now" already shows, so it is left out. Recomputed on each
  // refresh so new hours join.
  const hours = useMemo(() => {
    if (layers.air) return hoursWithData(stations).slice(0, -1);
    if (layers.wind)
      return todayHours(wind.fetchedAt ?? undefined).slice(0, -1);
    return [];
  }, [layers.air, layers.wind, stations, wind.fetchedAt]);

  // Only worth showing with at least one past hour to go back to
  const canScrub = hours.length > 0;

  // Go live again when the timeline is hidden or the chosen hour drops out
  const outOfRange = at !== null && !hours.includes(at);
  useEffect(() => {
    if (outOfRange) setAt(null);
  }, [outOfRange]);

  return (
    <section className="aq-map" aria-label="Air quality map">
      <div className="aq-map__stage">
        <MapContainer
          className="aq-map__canvas"
          center={REYKJAVIK}
          zoom={10}
          minZoom={6}
          maxZoom={16}
          maxBounds={ICELAND_BOUNDS}
          maxBoundsViscosity={0.8}
          zoomControl={false}
        >
          {/* Bottom to top: basemap (tile pane), drift (overlay pane), wind,
              place names, stations, popups */}
          <WMSTileLayer
            key={theme}
            url={LMI_WMS_URL}
            params={basemapParams}
            tileSize={512}
            attribution={LMI_ATTRIBUTION}
          />

          <Pane name="noise" style={{ zIndex: 250 }}>
            {layers.noise && (
              <WMSTileLayer
                key={layers.noise}
                url={NOISE_WMS_URL}
                params={NOISE_PARAMS[layers.noise]}
                tileSize={512}
                opacity={0.5}
                attribution={NOISE_ATTRIBUTION}
              />
            )}
          </Pane>

          {drifting.flatMap((m) =>
            driftAreas(m.lat, m.lon, m.wind).map((points, i) => (
              <Polygon
                key={`${m.local_id}-drift-${i}`}
                positions={points}
                interactive={false}
                pathOptions={{
                  stroke: false,
                  fillColor: m.severity.color,
                  fillOpacity: driftOpacity(m.wind.speed),
                }}
              />
            )),
          )}
          {drifting.map((m) => {
            const line = driftLine(m.lat, m.lon, m.wind);
            return line ? (
              <Polyline
                key={`${m.local_id}-${m.severity.level}-drift-line`}
                positions={line}
                interactive={false}
                className={`aq-drift aq-drift--${driftPace(m.wind.speed)}`}
                pathOptions={{
                  color: m.severity.color,
                  weight: 1.5,
                  lineCap: "round",
                  dashArray: "1 15",
                }}
              />
            ) : null;
          })}

          <Pane name="wind" style={{ zIndex: 420 }}>
            {layers.wind && <WindField weather={activeWeather} pane="wind" />}
          </Pane>

          <Pane name="labels" style={{ zIndex: 450 }}>
            <PlaceLabels />
          </Pane>

          <Pane name="soil" style={{ zIndex: 470 }}>
            {layers.soil && soilSites && <SoilSites sites={soilSites} />}
          </Pane>

          <Pane name="sale" style={{ zIndex: 480 }}>
            {layers.sale && google.user && sale.data && (
              <SaleListings listings={sale.data.listings} />
            )}
          </Pane>

          <Pane name="roads" style={{ zIndex: 490 }}>
            {layers.roads && roads.data && (
              <RoadStations stations={roads.data.stations} />
            )}
          </Pane>

          <Pane name="stations" style={{ zIndex: 500 }}>
            {layers.pollen && <PollenSites />}
            {layers.air &&
              markers.map((m) => (
                <CircleMarker
                  key={`${m.local_id}-${m.severity.level}`}
                  center={[m.lat, m.lon]}
                  radius={8}
                  className={
                    m.severity.level === null ? "aq-dot aq-dot--none" : "aq-dot"
                  }
                  pathOptions={{
                    weight: 2,
                    fillColor: m.severity.color,
                    fillOpacity: 1,
                  }}
                >
                  <Popup
                    pane="popupPane"
                    closeButton={false}
                    minWidth={280}
                    maxWidth={320}
                  >
                    <StationPopup station={m} />
                  </Popup>
                </CircleMarker>
              ))}
          </Pane>

          <ZoomControl position="bottomright" />
        </MapContainer>

        <header className="aq-panel aq-panel--title">
          <p className="aq-panel__eyebrow">
            Loftgæði
            {at !== null ? (
              <span className="aq-mono"> · Today {formatHour(at)}</span>
            ) : (
              updatedAt && (
                <span className="aq-mono"> · {formatTime(updatedAt)}</span>
              )
            )}
          </p>
          <h1 className="aq-panel__title">Air over Iceland</h1>
          {layers.air && <Summary markers={markers} />}
          {layers.wind && <WindNote wind={reykjavikWind} />}
          {error && (
            <p className="aq-panel__error" role="alert">
              Could not load {error} data. Trying again in a minute.
            </p>
          )}
        </header>

        <LayerPanel
          layers={layers}
          setLayer={setLayer}
          counts={counts}
          stationCount={markers.length}
          soilCount={soilSites?.length}
          soilError={soilError}
          google={google}
          sale={sale}
          roads={roads}
        />

        {/* Only air quality and wind change through the day */}
        {canScrub && (
          <Timeline
            hours={hours}
            value={at}
            onChange={setAt}
            loading={at !== null && windTodayLoading && !windToday}
          />
        )}
      </div>

      <p className="aq-map__note">
        Air quality from UST (api.ust.is), wind from Veðurstofa Íslands
        (api.vedur.is). Severity uses European Air Quality Index bands; H
        <sub>2</sub>S bands are an approximation. Arrows show which way the air
        is moving, estimated from the nearest weather stations; longer arrows
        mean stronger wind. The timeline replays today hour by hour, using
        hourly wind observations. Noise maps (2022, CNOSSOS-EU) and the
        contaminated soil register are from Umhverfis- og orkustofnun. Drift
        shows where the air measured at stations rated fair or worse is heading
        over the next half hour. It is an indication, not a dispersion model.
      </p>
    </section>
  );
};
